// Iframe Communication Utility - Overhauled Version
// Simplified, robust measurement using ResizeObserver on a single root element

import { getActivePinia } from 'pinia'
import { useAuthStore } from '@/stores/auth'

export interface IframeMessage {
  type: string;
  data?: Record<string, unknown>;
}

type MessageHandler = (data?: Record<string, unknown>) => void;

export class IframeCommunication {
  private isInIframe: boolean;
  private resizeObserver: ResizeObserver | null = null;
  private lastHeight = 0;
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;
  private listeners: Map<string, Set<MessageHandler>> = new Map();
  private parentReadyReceived = false;

  constructor() {
    this.isInIframe = window !== window.parent;
    this.setupMessageListener();
  }

  private shouldScrollParent(): boolean {
    try {
      const searchParams = new URLSearchParams(window.location.search)
      const hash = window.location.hash
      const hashIndex = hash.indexOf('?')
      const hashParams = hashIndex >= 0 ? new URLSearchParams(hash.slice(hashIndex + 1)) : null
      const value = searchParams.get('allow-parent-scroll') || hashParams?.get('allow-parent-scroll')
      return value === 'true' || value === '1'
    } catch {
      return false
    }
  }

  public isRunningInIframe(): boolean {
    return this.isInIframe;
  }

  /** True after the host sent parent-ready (catalog may defer until this arrives). */
  public hasReceivedParentReady(): boolean {
    return this.parentReadyReceived;
  }

  public sendMessage(type: string, data?: Record<string, unknown>): void {
    if (!this.isInIframe) return;
    
    // Ensure data is cloneable for postMessage by stripping any non-serializable content
    // This also handles Vue reactive proxies
    let cloneableData = data;
    if (data !== undefined) {
      try {
        cloneableData = JSON.parse(JSON.stringify(data));
      } catch (e) {
        console.warn('Failed to clone data for iframe message:', e);
        // Fallback to a simpler object if possible, or just the original
        // if JSON.stringify fails (e.g. circular refs), postMessage will still throw
      }
    }
    
    window.parent.postMessage({ type, data: cloneableData }, '*');
  }

  /**
   * The single source of truth for height updates.
   * Sends both modern and legacy formats for backward compatibility.
   */
  public sendHeightUpdate(height?: number): void {
    if (!this.isInIframe) return;

    if (this.debounceTimer) clearTimeout(this.debounceTimer);

    this.debounceTimer = setTimeout(() => {
      const currentHeight = height || this.measureHeight();
      if (Math.abs(currentHeight - this.lastHeight) < 2) return;

      this.lastHeight = currentHeight;

      // 1. Modern Format (v3.0+)
      this.sendMessage('height-update', { height: currentHeight });

      // 2. Legacy: style-update format
      this.sendMessage('style-update', { style: { height: currentHeight } });

      // 3. Legacy: resize-event format
      this.sendMessage('resize-event', {
        events: { windowResize: [{ height: currentHeight }] }
      });

      // 4. Legacy: Stringified JSON packet (for extremely old integrations)
      try {
        window.parent.postMessage(JSON.stringify({
          type: 'height-update',
          data: { height: currentHeight },
          style: { height: currentHeight },
          events: { windowResize: [{ height: currentHeight }] }
        }), '*');
      } catch (e) { /* ignore */ }
    }, 60);
  }

  private measureHeight(): number {
    // We target #iframe-root or #app. 
    // offsetHeight is generally more stable than scrollHeight for iframes.
    const root = document.getElementById('iframe-root') || document.getElementById('app');
    if (!root) return document.body.offsetHeight;

    // Use getBoundingClientRect for sub-pixel accuracy, then ceil
    const rect = root.getBoundingClientRect();
    return Math.ceil(rect.height);
  }

  /**
   * Sends a height update immediately, bypassing the debounce.
   * Useful for layout shifts that require an instant response (like "Load More").
   */
  public sendImmediateHeightUpdate(): void {
    if (!this.isInIframe) return;
    const currentHeight = this.measureHeight();
    this.lastHeight = currentHeight;
    
    this.sendMessage('height-update', { height: currentHeight });
    this.sendMessage('style-update', { style: { height: currentHeight } });
    this.sendMessage('resize-event', {
      events: { windowResize: [{ height: currentHeight }] }
    });
  }

  public sendReady(): void {
    if (!this.isInIframe) return;
    this.sendMessage('iframe-ready', { 
      url: window.location.href,
      height: this.measureHeight() 
    });
    this.sendHeightUpdate();
  }

  /**
   * Request login from the host.
   * - Dispatches `bitville:login-request` on this window (iframe document) for hosts that attach
   *   `contentWindow.addEventListener('bitville:login-request', ...)`.
   * - Same-origin: also dispatches on `window.parent` for hosts that listen on the parent page.
   * - Cross-origin: `postMessage` with type `login-request` remains the transport.
   */
  public sendLoginRequest(reason?: string): void {
    const data: Record<string, unknown> = {}
    if (reason !== undefined) {
      data.reason = reason
    }

    try {
      window.dispatchEvent(
        new CustomEvent('bitville:login-request', {
          bubbles: true,
          detail: { ...data },
        })
      )
    } catch (e) {
      console.warn('[Bitville] bitville:login-request dispatch on frame window failed:', e)
    }

    if (this.isInIframe && window.parent !== window) {
      try {
        window.parent.dispatchEvent(
          new CustomEvent('bitville:login-request', {
            bubbles: true,
            detail: { ...data },
          })
        )
      } catch {
        // Same-origin parent only; cross-origin throws SecurityError — expected.
      }
    }

    this.sendMessage('login-request', data)
  }

  public sendScrollToTop(): void {
    if (!this.isInIframe) return;
    if (!this.shouldScrollParent()) return;
    this.sendMessage('scroll-request', { position: 'point', y: 0 });
    window.parent.postMessage({ scroll: { position: 'point', y: 0 } }, '*');
  }

  public sendRouteChange(route: string, title?: string): void {
    this.sendMessage('route-changed', { route, title });
    if (this.shouldScrollParent()) {
      this.sendMessage('scroll-request', { position: 'point', y: 0 });
      window.parent.postMessage({ scroll: { position: 'point', y: 0 } }, '*');
    }
    
    // Force a few updates as Vue swaps components
    [50, 200, 500].forEach(delay => {
      setTimeout(() => this.sendHeightUpdate(), delay);
    });
  }

  /**
   * Modern measurement using ResizeObserver.
   * We only watch the main container.
   */
  public startObserving(elementId = 'app'): void {
    if (!this.isInIframe || !window.ResizeObserver) return;

    this.stopObserving();

    const target = document.getElementById(elementId);
    if (!target) return;

    this.resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        // Use borderBoxSize if available, fallback to contentRect
        const height = entry.borderBoxSize?.[0]?.blockSize ?? entry.contentRect.height;
        this.sendHeightUpdate(Math.ceil(height));
      }
    });

    this.resizeObserver.observe(target);
  }

  public stopObserving(): void {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
  }

  /**
   * Accept object or JSON string (some hosts stringify postMessage payloads).
   */
  private parseMessagePayload(raw: unknown): Record<string, unknown> | null {
    if (raw == null) return null
    if (typeof raw === 'string') {
      try {
        const parsed = JSON.parse(raw) as unknown
        if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
          return parsed as Record<string, unknown>
        }
        return null
      } catch {
        return null
      }
    }
    if (typeof raw === 'object' && !Array.isArray(raw)) {
      return raw as Record<string, unknown>
    }
    return null
  }

  /**
   * Color mode may be nested under `data` or set on the message root.
   */
  private mergeColorModeFields(
    root: Record<string, unknown>,
    nested?: Record<string, unknown>
  ): Record<string, unknown> {
    const n = nested && typeof nested === 'object' ? nested : {}
    const mode =
      n.mode ??
      n.colorMode ??
      root.mode ??
      root.colorMode ??
      root.theme
    return { ...n, mode }
  }

  private setupMessageListener(): void {
    window.addEventListener('message', (event) => {
      const root = this.parseMessagePayload(event.data)
      if (!root) return

      const type = typeof root.type === 'string' ? root.type : ''
      let nested = root.data
      if (typeof nested === 'string') {
        const inner = this.parseMessagePayload(nested)
        nested = inner ?? undefined
      }
      const data =
        nested && typeof nested === 'object' && !Array.isArray(nested)
          ? (nested as Record<string, unknown>)
          : undefined

      switch (type) {
        case 'parent-ready':
          this.handleParentReady(data);
          break;
        case 'bv-color-mode':
        case 'bitville-color-mode':
        case 'bv-set-mode':
          this.applyParentColorMode(this.mergeColorModeFields(root, data));
          break;
        case 'resize-request':
          this.sendHeightUpdate();
          break;
        case 'navigate':
          this.emit('navigate', data);
          break;
        case 'scroll-request':
          this.emit('scroll-request', data);
          break;
        default:
          this.emit(type, data);
      }
    });
  }

  private handleParentReady(data?: Record<string, unknown>): void {
    this.parentReadyReceived = true;
    if (data?.config && typeof data.config === 'object') {
      const config = data.config as Record<string, unknown>;

      // Apply tenant account before emit so early / in-flight catalog requests get auth-account.
      useAuthStore().applyParentConfig(config);

      // Immediate API Base URL update
      if (config.GAME_SERVER_URL && typeof config.GAME_SERVER_URL === 'string') {
        import('@/api').then(({ setApiBaseUrl }) => {
          setApiBaseUrl(config.GAME_SERVER_URL as string);
        });
      }

      const colorMode = config.colorMode ?? config.themeMode;
      if (
        colorMode === 'light' ||
        colorMode === 'dark' ||
        colorMode === 'system' ||
        colorMode === 'auto'
      ) {
        this.applyParentColorMode({
          mode: colorMode === 'auto' ? 'system' : colorMode,
        });
      }
    }
    this.emit('parent-ready', data);
  }

  /**
   * Apply light/dark from parent (postMessage). Canonical: { type: 'bv-color-mode', data: { mode: 'light' | 'dark' } }.
   * If the user used the in-app toggle, messages are ignored unless `data.force === true` (then override is cleared).
   * Parent can also pass config.colorMode / config.themeMode on parent-ready.
   */
  private applyParentColorMode(
    data?: Record<string, unknown>,
    piniaAttempt = 0
  ): void {
    if (!this.isInIframe) return;

    const raw = data?.mode ?? data?.colorMode;
    const normalized =
      typeof raw === 'string' ? raw.trim().toLowerCase() : '';
    if (
      normalized !== 'light' &&
      normalized !== 'dark' &&
      normalized !== 'system' &&
      normalized !== 'auto'
    ) {
      return;
    }

    if (!getActivePinia()) {
      if (piniaAttempt < 40) {
        window.setTimeout(
          () => this.applyParentColorMode(data, piniaAttempt + 1),
          50
        );
      }
      return;
    }

    const resolved = normalized === 'auto' ? 'system' : normalized;

    void import('@/stores/theme').then(({ useThemeStore }) => {
      try {
        const store = useThemeStore();
        const forced =
          data &&
          typeof data === 'object' &&
          'force' in data &&
          (data as { force?: unknown }).force === true;
        if (store.userOverrodeColorMode && !forced) {
          return;
        }
        if (forced) {
          store.clearUserColorModeOverride();
        }
        if (resolved === 'system') {
          store.enableSystemColorScheme();
          void import('@/utils/systemColorSchemeWatcher').then(
            ({ syncSystemColorSchemeListener }) => {
              syncSystemColorSchemeListener();
            }
          );
          void import('@/utils/syncColorModeToUrl').then(({ syncColorModeToUrl }) =>
            syncColorModeToUrl('system')
          );
        } else {
          store.setMode(resolved);
          void import('@/utils/syncColorModeToUrl').then(({ syncColorModeToUrl }) =>
            syncColorModeToUrl(resolved)
          );
        }
      } catch (e) {
        console.warn('Parent color mode: could not apply theme', e);
      }
    });
  }

  public on(type: string, handler: MessageHandler): () => void {
    let handlers = this.listeners.get(type);
    if (!handlers) {
      handlers = new Set();
      this.listeners.set(type, handlers);
    }
    handlers.add(handler);
    return () => this.off(type, handler);
  }

  public off(type: string, handler: MessageHandler): void {
    this.listeners.get(type)?.delete(handler);
  }

  private emit(type: string, data?: Record<string, unknown>): void {
    this.listeners.get(type)?.forEach(h => h(data));
  }

  public destroy(): void {
    this.stopObserving();
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
  }
}

export const iframeCommunication = new IframeCommunication();
